/**
 * Theme Configuration – Full design token set stored in Firestore.
 * Admins can create/edit themes via the Theme Editor in Organization Settings.
 */
export interface ThemeConfig {
  id: string;
  orgId: string;
  name: string;                         // e.g. "Dark Canvas", "Corporate Blue"
  description?: string;
  baseTheme: 'dark' | 'light';         // Starting point

  tokens: ThemeTokens;

  // Management
  isDefault: boolean;                   // Main theme for all org users
  isPublished: boolean;                 // Visible for user selection
  createdBy: string;                    // userId
  createdAt: Date;
  updatedAt: Date;
}

export interface ThemeTokens {
  colors: ThemeColorTokens;
  borders: ThemeBorderTokens;
  radii: ThemeRadiiTokens;
  shadows: ThemeShadowTokens;
  typography: ThemeTypographyTokens;
  effects: ThemeEffectTokens;
}

export interface ThemeColorTokens {
  // Backgrounds (hex values – converted to RGB at runtime)
  bgBase: string;
  bgRaised: string;
  bgCard: string;
  bgCardHover: string;
  bgSurface: string;
  bgSurfaceHover: string;
  bgInput: string;

  // Text
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  textMuted: string;
  textInverted: string;

  // Accent
  accent: string;
  accentBright: string;

  // Primary CTA
  primary: string;
  primaryHover: string;
  primaryContrast: string;

  // Semantic
  success: string;
  warning: string;
  danger: string;
  info: string;

  // AI Gradient
  aiFrom: string;
  aiTo: string;
}

export interface ThemeBorderTokens {
  subtleOpacity: number;                // 0.04
  defaultOpacity: number;               // 0.07
  strongOpacity: number;                // 0.12
  accentOpacity: number;                // 0.25
  width: string;                        // '1px'
}

export interface ThemeRadiiTokens {
  xs: string;                           // '8px'
  sm: string;                           // '10px'
  md: string;                           // '14px'
  lg: string;                           // '18px'
  xl: string;                           // '22px'
  pill: string;                         // '9999px'
}

export interface ThemeShadowTokens {
  card: string;
  lg: string;
  glowAccent: string;
  glowSuccess: string;
  glowWarning: string;
}

export interface ThemeTypographyTokens {
  fontDisplay: string;                  // 'Sora'
  fontBody: string;                     // 'Plus Jakarta Sans'
  fontMono: string;                     // 'IBM Plex Mono'
  baseFontSize: string;                 // '16px'
  lineHeight: string;                   // '1.55'
}

export interface ThemeEffectTokens {
  springBounce: string;
  springSmooth: string;
  springSnappy: string;
  gradientGlow: string;
  gradientGlowBlue: string;
  iconStrokeWidth: string;              // '1.5'
}

// ─── Preset themes ─────────────────────────────────────────

export interface ThemePreset {
  name: string;
  description: string;
  baseTheme: 'dark' | 'light';
  tokens: ThemeTokens;
}

// ─── Utilities ─────────────────────────────────────────────

/**
 * Convert hex color (#RRGGBB) to RGB raw format ("R G B")
 * for Tailwind CSS variable compatibility.
 */
export function hexToRgbRaw(hex: string): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  return `${r} ${g} ${b}`;
}

/**
 * Convert RGB raw format ("R G B") back to hex (#RRGGBB).
 */
export function rgbRawToHex(rgb: string): string {
  const parts = rgb.trim().split(/\s+/).map(Number);
  if (parts.length !== 3) return '#000000';
  return '#' + parts.map(p => Math.min(255, Math.max(0, p)).toString(16).padStart(2, '0')).join('');
}

/**
 * Check WCAG 2.1 contrast ratio between two hex colors.
 * Returns ratio (>= 4.5 is AA for normal text, >= 3.0 for large text).
 */
export function getContrastRatio(hex1: string, hex2: string): number {
  const lum1 = getRelativeLuminance(hex1);
  const lum2 = getRelativeLuminance(hex2);
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  return (lighter + 0.05) / (darker + 0.05);
}

function getRelativeLuminance(hex: string): number {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16) / 255;
  const g = parseInt(clean.substring(2, 4), 16) / 255;
  const b = parseInt(clean.substring(4, 6), 16) / 255;

  const sR = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
  const sG = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
  const sB = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);

  return 0.2126 * sR + 0.7152 * sG + 0.0722 * sB;
}

/**
 * Convert a ThemeConfig's color tokens to CSS custom properties
 * (RGB raw format for colors, direct values for others).
 */
export function themeTokensToCssVars(tokens: ThemeTokens): Record<string, string> {
  const vars: Record<string, string> = {};
  const c = tokens.colors;

  // Background colors → RGB raw
  vars['--bg-base'] = hexToRgbRaw(c.bgBase);
  vars['--bg-raised'] = hexToRgbRaw(c.bgRaised);
  vars['--bg-card'] = hexToRgbRaw(c.bgCard);
  vars['--bg-card-hover'] = hexToRgbRaw(c.bgCardHover);
  vars['--bg-surface'] = hexToRgbRaw(c.bgSurface);
  vars['--bg-surface-hover'] = hexToRgbRaw(c.bgSurfaceHover);
  vars['--bg-input'] = hexToRgbRaw(c.bgInput);

  // Text colors → RGB raw
  vars['--text-primary'] = hexToRgbRaw(c.textPrimary);
  vars['--text-secondary'] = hexToRgbRaw(c.textSecondary);
  vars['--text-tertiary'] = hexToRgbRaw(c.textTertiary);
  vars['--text-muted'] = hexToRgbRaw(c.textMuted);
  vars['--text-inverted'] = hexToRgbRaw(c.textInverted);

  // Accent → RGB raw
  vars['--accent'] = hexToRgbRaw(c.accent);
  vars['--accent-bright'] = hexToRgbRaw(c.accentBright);

  // Accent opacity variants (derived)
  const [aR, aG, aB] = c.accent.replace('#', '').match(/.{2}/g)!.map(h => parseInt(h, 16));
  vars['--accent-soft'] = `rgba(${aR}, ${aG}, ${aB}, 0.10)`;
  vars['--accent-medium'] = `rgba(${aR}, ${aG}, ${aB}, 0.18)`;
  vars['--accent-glow'] = `rgba(${aR}, ${aG}, ${aB}, 0.30)`;

  // Primary CTA → RGB raw
  vars['--color-primary'] = hexToRgbRaw(c.primary);
  vars['--color-primary-hover'] = hexToRgbRaw(c.primaryHover);
  vars['--color-primary-contrast'] = hexToRgbRaw(c.primaryContrast);

  // Semantic → RGB raw + opacity variants
  const semanticColors = [
    { key: 'success', hex: c.success },
    { key: 'warning', hex: c.warning },
    { key: 'danger', hex: c.danger },
    { key: 'info', hex: c.info },
  ];
  for (const { key, hex } of semanticColors) {
    vars[`--color-${key}`] = hexToRgbRaw(hex);
    const [r, g, b] = hex.replace('#', '').match(/.{2}/g)!.map(h => parseInt(h, 16));
    vars[`--color-${key}-soft`] = `rgba(${r}, ${g}, ${b}, 0.10)`;
    vars[`--color-${key}-medium`] = `rgba(${r}, ${g}, ${b}, 0.15)`;
  }

  // AI Gradient
  vars['--color-ai-from'] = c.aiFrom;
  vars['--color-ai-to'] = c.aiTo;

  // Borders
  const b2 = tokens.borders;
  const borderBase = tokens.colors.bgBase === '#0C0C14' ? '255, 255, 255' : '0, 0, 0';
  vars['--border-subtle'] = `rgba(${borderBase}, ${b2.subtleOpacity})`;
  vars['--border-default'] = `rgba(${borderBase}, ${b2.defaultOpacity})`;
  vars['--border-strong'] = `rgba(${borderBase}, ${b2.strongOpacity})`;
  vars['--border-accent'] = `rgba(${aR}, ${aG}, ${aB}, ${b2.accentOpacity})`;
  vars['--border-width'] = b2.width;

  // Radii
  const rd = tokens.radii;
  vars['--radius-xs'] = rd.xs;
  vars['--radius-sm'] = rd.sm;
  vars['--radius-md'] = rd.md;
  vars['--radius-lg'] = rd.lg;
  vars['--radius-xl'] = rd.xl;
  vars['--radius-pill'] = rd.pill;

  // Shadows
  const sh = tokens.shadows;
  vars['--shadow-card'] = sh.card;
  vars['--shadow-lg'] = sh.lg;
  vars['--shadow-glow-accent'] = sh.glowAccent;
  vars['--shadow-glow-success'] = sh.glowSuccess;
  vars['--shadow-glow-warning'] = sh.glowWarning;

  // Typography
  const ty = tokens.typography;
  vars['--font-display'] = `'${ty.fontDisplay}', system-ui, sans-serif`;
  vars['--font-body'] = `'${ty.fontBody}', system-ui, sans-serif`;
  vars['--font-mono'] = `'${ty.fontMono}', monospace`;

  // Effects
  const ef = tokens.effects;
  vars['--spring-bounce'] = ef.springBounce;
  vars['--spring-smooth'] = ef.springSmooth;
  vars['--spring-snappy'] = ef.springSnappy;
  vars['--icon-stroke-width'] = ef.iconStrokeWidth;

  if (ef.gradientGlow) vars['--gradient-glow'] = ef.gradientGlow;
  if (ef.gradientGlowBlue) vars['--gradient-glow-blue'] = ef.gradientGlowBlue;

  return vars;
}
